import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, X } from "lucide-react";
import Checkbox from "./ui/Checkbox/Checkbox";
import "./MultiSelectDropdown.css";

const MultiSelectDropdown = ({
  label,
  options = [],
  selected = [],
  onChange,
  placeholder = "Select...",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState("below");
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const trigger = dropdownRef.current;
      const triggerRect = trigger.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - triggerRect.bottom;
      const spaceAbove = triggerRect.top;
      const menuHeight = 350; // Approximate max height of menu

      if (spaceBelow < menuHeight && spaceAbove > spaceBelow) {
        setDropdownPosition("above");
      } else {
        setDropdownPosition("below");
      }
    }
  }, [isOpen]);

  // Helper to get option value (handle both objects and strings)
  const getOptionValue = (option) => {
    if (typeof option === 'string') return option;
    return option.value || option.id || option.label;
  };

  // Helper to get option label (handle both objects and strings)
  const getOptionLabel = (option) => {
    if (typeof option === 'string') return option;
    return option.label || option.value || option.id;
  };

  const handleToggle = (option) => {
    const currentSelected = selected || [];
    const optionValue = getOptionValue(option);
    if (currentSelected.includes(optionValue)) {
      onChange(currentSelected.filter((item) => item !== optionValue));
    } else {
      onChange([...currentSelected, optionValue]);
    }
  };

  const handleSelectAll = () => {
    const currentOptions = options || [];
    const currentSelected = selected || [];
    if (currentSelected.length === currentOptions.length) {
      onChange([]);
    } else {
      onChange(currentOptions.map(getOptionValue));
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  const getDisplayText = () => {
    const currentSelected = (selected || []).filter(s => s && (typeof s === 'string' ? s.trim() !== "" : true));
    const currentOptions = options || [];
    console.log("🎯 MultiSelectDropdown getDisplayText - selected:", selected, "-> filtered:", currentSelected);
    if (currentSelected.length === 0) return placeholder;
    if (currentOptions.length > 0 && currentSelected.length === currentOptions.length) return "All selected";

    // Map selected values to their labels
    const labels = currentSelected.map(selectedValue => {
      const matchedOption = currentOptions.find(opt => getOptionValue(opt) === selectedValue);
      return matchedOption ? getOptionLabel(matchedOption) : selectedValue;
    });

    const result = labels.join(", ");
    console.log("🎯 MultiSelectDropdown getDisplayText - returning:", result);
    return result;
  };

  return (
    <div className="multi-select-dropdown" ref={dropdownRef}>
      {label && <label className="multi-select-label">{label}</label>}
      <div
        className={`multi-select-trigger ${isOpen ? "open" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={`multi-select-value ${(!selected || selected.length === 0) ? 'placeholder' : ''}`}>{getDisplayText()}</span>
        <div className="multi-select-actions">
          {selected && selected.length > 0 && (
            <button
              className="multi-select-clear"
              onClick={handleClear}
              title="Clear selection"
            >
              <X size={14} />
            </button>
          )}
          <ChevronDown
            size={16}
            className={`multi-select-arrow ${isOpen ? "open" : ""}`}
          />
        </div>
      </div>

      {isOpen && (
        <div 
          ref={menuRef}
          className={`multi-select-menu ${dropdownPosition === "above" ? "position-above" : ""}`}
        >
          <div className="multi-select-menu-header">
            <button className="multi-select-all" onClick={handleSelectAll}>
              {selected && options && selected.length === options.length
                ? "Deselect All"
                : "Select All"}
            </button>
          </div>
          <div className="multi-select-options">
            {options && options.map((option) => {
              const optionValue = getOptionValue(option);
              const optionLabel = getOptionLabel(option);
              return (
                <div key={optionValue} className="multi-select-option">
                  <Checkbox
                    label={optionLabel}
                    checked={selected && selected.includes(optionValue)}
                    onChange={() => handleToggle(option)}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
